import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import TurnstileWidget from "@/components/TurnstileWidget";

const TURNSTILE_ENABLED = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

/**
 * ForgotPassword
 * --------------
 * Enter email → request a reset link. Backend returns a neutral 200 either way
 * (to prevent account enumeration), so we always show the same "check your
 * inbox" confirmation regardless of whether the email actually exists.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_ENABLED && !turnstileToken) {
      toast.error("Please complete the verification challenge.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email, turnstile_token: turnstileToken || null });
      setSent(true);
    } catch (err) {
      // Backend almost always returns 200 for this endpoint; this branch only
      // fires for genuine network / 5xx errors.
      toast.error(err?.response?.data?.detail || "Could not send reset email");
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900 p-6" data-testid="forgot-password-page">
      <Card className="w-full max-w-md p-8 sm:p-10 border-slate-200">
        {sent ? (
          <div className="text-center" data-testid="forgot-sent">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-100 grid place-items-center mb-4">
              <Mail className="w-7 h-7 text-emerald-700" />
            </div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Check your inbox</h1>
            <p className="text-slate-600 mt-2">
              If an account exists for <span className="font-semibold text-slate-900">{email}</span>, we just sent it a password reset link. It expires in 60 minutes.
            </p>
            <div className="mt-5 p-3 rounded-lg bg-amber-50 border border-amber-200 text-left text-sm text-amber-800">
              Don't see it? Check your <span className="font-semibold">Spam</span> or <span className="font-semibold">Promotions</span> folder.
            </div>
            <Link
              to="/login"
              className="block mt-5 text-sm text-blue-700 font-semibold hover:underline"
              data-testid="forgot-back-login"
            >
              Back to login
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold tracking-tight">Forgot password?</h1>
            <p className="text-slate-600 mt-2 text-sm">
              Enter the email tied to your PrepMasters account. We'll send you a link to set a new password.
            </p>
            <form onSubmit={submit} className="space-y-4 mt-6">
              <div>
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-11"
                disabled={loading || (TURNSTILE_ENABLED && !turnstileToken)}
                data-testid="forgot-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
              </Button>
              <TurnstileWidget ref={turnstileRef} onToken={setTurnstileToken} action="forgot_password" />
            </form>
            <p className="mt-6 text-sm text-slate-600 text-center">
              Remembered it?{" "}
              <Link to="/login" className="text-blue-700 font-semibold hover:underline">Login</Link>
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
